import React, { useState, useContext, useCallback, useRef, useMemo } from 'react';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import { isMappingUnknown, getEditorConfigValue } from 'components/property-editors/utils/EditorConfigurationUtils';
import './UnknownMappingField.less';
import classNames from 'classnames';
import { ValidationIssue } from '@decisions/server/DecisionsFramework/ValidationIssue';
import { ContextProviderNode } from '@decisions/server/DecisionsFramework/Design/Properties';
import { SearchBox } from './SearchBox';
import {
  getSuggestions,
  setMapping,
} from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/Mapping/MappingDataService';
import { DropDownOption } from './DropDownOption';
import { DataPair } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ContextData/DataPair';
import { debounce } from 'debounce';
import { InputMappingConstants } from 'components/property-editors/MappingConstants';
import {
  isString,
  isUnsignedInt,
  isNonNegativeIntValue,
  isSignedInt,
  isIntValue,
  isReal,
  isRealValue,
  isBoolean,
} from 'utils/TypeUtils';
import { stringContains } from 'utils/StringUtils';
import { HighlightMatch } from 'components/lists/HighlightMatch';

interface IUnknownMappingFieldProps extends React.HTMLProps<{}> {
  path: string[];
  child: ContextProviderNode;
  toDos?: boolean;
  validationIssues: ValidationIssue[];
  onPickClick: () => {};
  isList?: boolean;
}

const getPlaceHolder = (hasFocus: boolean) => (hasFocus ? ' ' : 'Type value/variable name or...');

const getFakeInputClassName = (validationIssues: any[]) =>
  classNames('dpc-unknown-mapping-field__fake-input', {
    'dpc-unknown-mapping-field__fake-input--error': !!validationIssues.length,
  });

const getInputClassName = (hasFocus: boolean, value: string) =>
  classNames('dpc-unknown-mapping-field__input', {
    'dpc-unknown-mapping-field__input--focused': hasFocus || value,
  });

const MAX_SEARCH_LEVELS = 3;

/**
 * A field for displaying and assigning an unknown mapping type.
 *
 * TODO this is actually specific to Property Grid, so it should probably live there.
 *
 * Copyright (c) 2019 Decisions ALl Rights Reserved
 */
export function UnknownMappingField({ child, path, isList, onPickClick, validationIssues }: IUnknownMappingFieldProps) {
  const [fieldValue, setFieldValue] = useState('');
  const { mappingContext, editingContextId, refreshContext } = useContext(PropertyEditorContext);
  const [hasFocus, setHasFocus] = useState(false);
  const entityTypeName = getEditorConfigValue(child!.Children![0], 'type');
  const constOption = useRef<MappingSuggestion>();

  const searchFn = useCallback(
    async (searchString: string) => {
      setFieldValue(searchString);
      if (!searchString) {
        return [];
      }
      const suggestions = await getSuggestions({
        mappingContext,
        maxLevels: MAX_SEARCH_LEVELS,
        searchString,
      });

      const options = buildSuggestList(searchString, suggestions, entityTypeName, isList);

      constOption.current =
        options.length && options[0].mappingType === InputMappingConstants.MAPPING_CONSTANT ? options[0] : undefined;

      return options;
    },
    [entityTypeName, isList, mappingContext]
  );

  const onPickItem = async (_id: string, item: MappingSuggestion) => {
    if (!item) {
      // it's possible to trigger this when no items are available, e.g. search string is empty.
      if (fieldValue && constOption.current) {
        commitConstString.clear();
        commitConstString(fieldValue);
      }
      return;
    }
    await setMapping({
      mappingContext,
      editingContext: editingContextId,
      mappingType: item.mappingType,
      path,
      value: item.value,
    });
    refreshContext();
    commitConstString.clear();
  };

  const onItemChosen = (item: MappingSuggestion) => onPickItem('', item);

  /** using useCallback and debounce to avoid race between field blur and option click. */
  const commitConstString = useMemo(
    () =>
      debounce(async (value: string) => {
        await setMapping({
          mappingContext,
          editingContext: editingContextId,
          mappingType: getMappingTypeForString(value),
          path,
          value,
        });
        refreshContext();
      }, 300),
    // TODO is path going to update too often here?
    [editingContextId, mappingContext, path, refreshContext]
  );

  const onSearchBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setHasFocus(false);
    commitConstString.clear();
    if (constOption.current) {
      commitConstString(constOption.current.value);
    }
  };

  const onSearchFocus = useCallback(() => {
    setHasFocus(true);
  }, []);

  return child?.Children && isMappingUnknown(child.Children[0]) ? (
    <div className="dpc-unknown-mapping-field">
      <div className={getFakeInputClassName(validationIssues)}>
        <SearchBox
          placeholder={getPlaceHolder(hasFocus)}
          showSearchIcon={false}
          className={getInputClassName(hasFocus, fieldValue)}
          search={searchFn}
          onBlur={onSearchBlur}
          onFocus={onSearchFocus}
          onItemChosen={onItemChosen}
          itemTemplate={({ index, item, isFocused }) => (
            <DropDownOption
              key={item.label + index}
              index={index}
              onChosen={onPickItem}
              isFocused={isFocused}
              value={item}
            >
              <HighlightMatch searchString={fieldValue}>{item.label}</HighlightMatch>{' '}
              <span className="dpc-unknown-mapping-field__suggestion-type">{item.info}</span>
            </DropDownOption>
          )}
        />
        {!hasFocus && !fieldValue && (
          <button className="dpc-unknown-mapping-field__button" onClick={onPickClick}>
            Pick
          </button>
        )}
      </div>
    </div>
  ) : null;
}

interface MappingSuggestion {
  value: string;
  label: string;
  info: string;
  mappingType: string;
}

// overlooked some detail here:
export function buildSuggestList(
  fieldText: string,
  options: DataPair[],
  entityTypeName: string,
  isList?: boolean
): MappingSuggestion[] {
  const suggestions = options.map(toMappingSuggestItem);

  if (fieldText && !isList) {
    const constSuggestion = buildConstSuggestion(fieldText, entityTypeName);
    if (constSuggestion) {
      suggestions.unshift(constSuggestion);
    }
  }

  // TODO What was this extra level of filtering client-side for?
  // is the server not giving us what we need?
  return suggestions.filter(item => stringContains(item.label, fieldText, true));
}

// TODO move these helpers into a helper file/folder

export function getMappingTypeForString(fieldText: string) {
  switch (fieldText.toUpperCase()) {
    case 'NULL':
      return InputMappingConstants.MAPPING_NULL;
    case 'IGNORE':
      return InputMappingConstants.MAPPING_IGNORE;
    default:
      return InputMappingConstants.MAPPING_CONSTANT;
  }
}

export function buildConstSuggestion(fieldText: string, entityTypeName: string): MappingSuggestion | undefined {
  if ('NULL' === fieldText.toUpperCase()) {
    return { value: 'null', label: 'null', info: '', mappingType: InputMappingConstants.MAPPING_NULL };
  }

  if ('IGNORE' === fieldText.toUpperCase()) {
    return { value: 'ignore', label: 'ignore', info: '', mappingType: InputMappingConstants.MAPPING_IGNORE };
  }

  const suggestion: MappingSuggestion = {
    value: fieldText,
    label: fieldText,
    info: '(value)',
    mappingType: InputMappingConstants.MAPPING_CONSTANT,
  };

  if (isString(entityTypeName)) {
    suggestion.label = `"${fieldText}"`;
    return suggestion;
  } else if (isBoolean(entityTypeName) && isBooleanValue(fieldText)) {
    suggestion.label = getBooleanValue(fieldText);
  } else if (
    (isUnsignedInt(entityTypeName) && isNonNegativeIntValue(fieldText)) ||
    (isSignedInt(entityTypeName) && isIntValue(fieldText)) ||
    (isReal(entityTypeName) && isRealValue(fieldText))
  ) {
    return suggestion;
  }
  // if none of the above types match, we don't add a const suggestion.
}

function isBooleanValue(value: string): boolean {
  return stringContains('false', value, true) || stringContains('true', value, true);
}

/** Enforce an appropriate "boolean" string value. */
function getBooleanValue(value: string): string {
  if (stringContains('false', value, true)) {
    return 'false';
  }

  if (stringContains('true', value, false)) {
    return 'true';
  }

  throw new Error('incorrect value for boolean');
}

function toMappingSuggestItem(pair: DataPair): MappingSuggestion {
  return {
    value: pair.Name,
    label: pair.Name,
    info: '(pick)',
    mappingType: InputMappingConstants.MAPPING_SELECT,
  };
}
